import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import {
  isCoachingProgram,
  shouldHideProgressTracking,
} from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { DropdownMenuItem } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/dropdownButton/DropdownButton'

import { LT_COACH_ROLES } from '../../../../../common/constants/appConstants'
import { CLIENT_NOTES, CLIENT_PROGRESS } from '../../../../../common/constants/routingConstants'
import { getEpisodeDropdownItemText, hasRole } from '../../../../../common/utils/utils'
import { getAuthUserId } from '../../../../../data/auth/authSelectors'
import { getProviderDetailsData } from '../../../../../providers/data/providerSelectors'
import { isProviderEpisode } from '../../../../episodes/data/episodeParticipants'

export const useEpisodePickerDropdownItems = (
  episodes: Episode[],
  selectHandler: React.Dispatch<React.SetStateAction<Episode>>,
  clientDetailsSelectedEpisode?: Episode,
) => {
  /**
   * NOTE:
   *   Notes tab shows all episodes, i.e, not filtered by the provider id, that the backend returns.
   *   The episodes API will enforce the permissions/access so that providers only see episodes that they have a right to
   *   see.
   *   Ideally, the episode picker dropdown menu items should be sourced from either:
   *      1. episodes API without the provider id -> notes tab
   *      2. episodes API that filters by the provider id -> all non-Notes tabs
   *
   *   We're in the middle of migrating to using RTK Query, and the second option is not yet available.
   *   As such, since the episodes on the notes tab is a superset of all other tabs, we will filter the episodes
   *   on provider id depending on the current route.
   *
   *   The selection logic on the Notes tab will be handled in the Notes tab itself, i.e, it will have to listen to
   */
  const intl = useIntl()
  const { pathname } = useLocation()
  const userId = useSelector(getAuthUserId)
  const providerDetailsData = useSelector(getProviderDetailsData)
  const [episodeDropdownItems, setEpisodeDropdownItems] = useState<DropdownMenuItem[]>([])

  const episodesForCurrentProvider = useMemo(() => {
    return episodes.filter((episode) => isProviderEpisode(episode, userId))
  }, [episodes, userId])

  const episodesBasedOnCoachingRole = useMemo(() => {
    if (!providerDetailsData) {
      return []
    }

    const isCoaching = hasRole(providerDetailsData?.roles, LT_COACH_ROLES)
    return episodes.filter((episode: Episode) => {
      if (shouldHideProgressTracking(episode.program_name)) return false
      const episodeProgramIsCoaching = isCoachingProgram(episode.program_name)
      return isCoaching ? episodeProgramIsCoaching : !episodeProgramIsCoaching
    })
  }, [episodes, providerDetailsData])

  useEffect(() => {
    const toDropdownItem = (episode: Episode): DropdownMenuItem => ({
      text: getEpisodeDropdownItemText(intl, episode),
      id: episode.id,
      selectHandler: () => selectHandler(episode),
    })

    switch (pathname) {
      case CLIENT_NOTES.route:
        setEpisodeDropdownItems(episodes.map(toDropdownItem))
        break
      case CLIENT_PROGRESS.route:
        if (
          clientDetailsSelectedEpisode &&
          !episodesBasedOnCoachingRole.find((episode) => episode.id === clientDetailsSelectedEpisode.id)
        ) {
          setEpisodeDropdownItems([
            toDropdownItem(clientDetailsSelectedEpisode),
            ...episodesBasedOnCoachingRole.map(toDropdownItem),
          ])
          return
        }
        setEpisodeDropdownItems(episodesBasedOnCoachingRole.map(toDropdownItem))
        break
      default:
        setEpisodeDropdownItems(episodesForCurrentProvider.map(toDropdownItem))
        break
    }
  }, [
    pathname,
    episodes,
    selectHandler,
    episodesForCurrentProvider,
    episodesBasedOnCoachingRole,
    clientDetailsSelectedEpisode,
    intl,
  ])

  return episodeDropdownItems
}
